import GeneratedStory from '@app/Dashboard/pages/whatif/_components/generatedStory/GeneratedStory';
import RecordingVoice from '@app/Dashboard/pages/whatif/_components/recordingVoice/RecordingVoice';
import StartRecording from '@app/Dashboard/pages/tellStory/_components/startRecording/StartRecording';
import { GoChevronLeft } from 'react-icons/go';
import styles from './StoriesList.module.scss';
import emptyImage from '@assets/images/ai4-plan.png';
import {
  ITellStoryDetailRes,
  ITellStoryStoriesRes,
} from 'src/core/repo/tellStory/type';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { childListResponse } from 'src/core/repo/child/types';
import { MdChevronRight } from 'react-icons/md';
import { Button } from 'antd';

interface Props {
  setStep: (step: number) => void;
  stories?: ITellStoryDetailRes[];
  isLoading: boolean;
  setGeneratedText: (text: string) => void;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  generatedText: string;
  selectedChildren: childListResponse | null;
  selectedStory: ITellStoryStoriesRes | null;
  handleSubmit: (
    stories: ITellStoryDetailRes[],
    generatedTextById: Record<number, string>,
  ) => void;
}

const TOTAL_TIME_SECOND = 10 * 60;

const StoriesList = ({
  setStep,
  stories,
  isLoading,
  generatedText,
  setFile,
  setGeneratedText,
  selectedChildren,
  selectedStory,
  handleSubmit,
}: Props) => {
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [audioUrlById, setAudioUrlById] = useState<Record<number, string>>({});
  const [generatedTextById, setGeneratedTextById] = useState<
    Record<number, string>
  >({});
  const [isGeneratingById, setIsGeneratingById] = useState<
    Record<number, boolean>
  >({});
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const [activeStory, setActiveStory] = useState<null | number>(null);
  const [finishedStoryId, setFinishedStoryId] = useState<null | number>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);

  const startRecording = async (story: ITellStoryDetailRes) => {
    setActiveStory(Number(story.StoryContentID));
    setFinishedStoryId(null);
    setTimer(0);
    setAudioUrlById((prev) => ({ ...prev, [story.StoryContentID]: '' }));
    setGeneratedTextById((prev) => ({ ...prev, [story.StoryContentID]: '' }));
    setIsRecording(true);

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    mediaRecorder.current = recorder;
    audioChunks.current = [];

    recorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      setAudioUrlById((prev) => ({ ...prev, [story.StoryContentID]: url }));

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64data = reader.result?.toString().split(',')[1];
        if (base64data) {
          await sendVoice({
            file: base64data,
            story_id: story.StoryID,
            story_content_id: story.StoryContentID,
            child_id: selectedChildren?.ChildID,
          });
        }
      };
      reader.readAsDataURL(blob);
    };

    const sendVoice = async (data: any) => {
      setIsGeneratingById((prev) => ({
        ...prev,
        [story.StoryContentID]: true,
      }));
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/tellstory/voice-to-text`,
          data,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.status === 200 && response.data) {
          setGeneratedTextById((prev) => ({
            ...prev,
            [story.StoryContentID]: response.data.text ?? '',
          }));
        }
      } catch (error) {
        console.error('Upload error:', error);
      } finally {
        setIsGeneratingById((prev) => ({
          ...prev,
          [story.StoryContentID]: false,
        }));
      }
    };

    recorder.start();
    return stream;
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    setIsRecording(false);
    setFinishedStoryId(activeStory);
    setActiveStory(null);
  };

  useEffect(() => {
    if (isRecording) {
      intervalId.current = setInterval(
        () => setTimer((prev) => prev + 1),
        1000,
      );
    } else if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }

    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [isRecording]);

  useEffect(() => {
    if (isRecording && timer >= TOTAL_TIME_SECOND) stopRecording();
  }, [timer, isRecording]);

  const handleRestRecording = (storyId: number) => {
    setIsRecording(true);
    setAudioUrlById((prev) => ({ ...prev, [storyId]: '' }));
    setIsGeneratingById((prev) => ({ ...prev, [storyId]: false }));
  };

  const setUserStoryTextById = (storyId: number, text: string) => {
    setGeneratedTextById((prev) => ({ ...prev, [storyId]: text }));
  };

  return (
    <div className="container">
      <div className={styles.description_wrapper}>
        <button onClick={() => setStep(3)} className={styles.back_button}>
          <GoChevronLeft size={22} />
          <p>Back</p>
        </button>
        <h1>Tell Me Your Story</h1>
        <p>Let Your Child Narrate This Picture and Record Their Voice</p>
      </div>

      <div className={styles.storiesWrapper}>
        {stories?.map((story) => (
          <div className={styles.story_card} key={story.StoryContentID}>
            <div className={styles.story_card_image}>
              <img
                style={{ objectFit: story.ImagePath ? 'cover' : 'contain' }}
                src={story.ImagePath || emptyImage}
                alt={story.TopicTitle}
              />
            </div>

            {activeStory === story.StoryContentID &&
            isRecording &&
            !audioUrlById[story.StoryContentID] ? (
              <RecordingVoice
                isGenerating={!!isGeneratingById[story.StoryContentID]}
                stopRecording={stopRecording}
                timer={timer}
                total={TOTAL_TIME_SECOND}
              />
            ) : audioUrlById[story.StoryContentID] ? (
              <GeneratedStory
                audioUrl={audioUrlById[story.StoryContentID]}
                handleRestRecording={() =>
                  handleRestRecording(story.StoryContentID)
                }
                setStep={setStep}
                isGenerating={!!isGeneratingById[story.StoryContentID]}
                userStoryText={generatedTextById[story.StoryContentID]}
                setUserStoryText={(text: string) =>
                  setUserStoryTextById(story.StoryContentID, text)
                }
              />
            ) : (
              <StartRecording
                setFile={setFile}
                startRecording={() => void startRecording(story)}
                selectedStory={selectedStory}
              />
            )}
          </div>
        ))}

        <div className={styles.buttonWrapper}>
          <Button
            className={styles.submitButton}
            onClick={() => {
              void handleSubmit(stories ?? [], generatedTextById);
            }}>
            <p>Export or Share</p>
            <MdChevronRight size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StoriesList;
